/**
 * useCompanionName — the *coach* name (what the family talks to).
 *
 * Resolution order:
 *   1. Staff roles (TRFer + admin) always see the staff copilot's name,
 *      `COPILOT_NAME` ("William", #3066) — no family context applies, and both
 *      roles are backed by the same williams caseload agent on
 *      `/api/trfer/williams/*` (requireTRFerOrAdmin).
 *   2. Otherwise, derive from the selected voice persona's display name
 *      (e.g. "Maya - The Practical Helper" → "Maya"). This is the chosen
 *      voice character — the actual conversational identity.
 *   3. If no persona selected yet (or persona prefs not loaded), fall
 *      back to the platform default ("Maya").
 *
 * Step 3 folds two different situations into one string: "this family chose
 * nothing" and "we have not asked yet". Surfaces that paint the name as
 * IDENTITY — the voice PiP disc initial — must tell them apart, so
 * `useCompanionNameState()` below reports the in-flight moment as
 * `{ name: null, isLoading: true }` (#1806).
 *
 * The free-text `voice_coach_character.name` override is no longer
 * consulted — the coach name IS the voice character.
 *
 * Platform identity (the sidebar brand chip, the push-banner fallback
 * title) is a separate concept — import COMPANION_NAME from
 * `@/lib/companion-config` directly for those surfaces.
 *
 * The Settings "…'s Voice" card is NOT platform identity: it names the
 * voice the family's own companion speaks in, so it takes this hook's
 * family-scoped value (#639, design-grammar §4 lexicon).
 */
import { useAuth } from '@/contexts/AuthContext';
import { usePersonaPreferences } from './usePersonaPreferences';
import { COMPANION_NAME, COPILOT_NAME } from '@/lib/companion-config';

export interface CompanionNameState {
  /**
   * The resolved companion name, or `null` while the persona preferences
   * query is still in flight — the family HAS a persona, we just do not know
   * it yet. Never `null` for staff (they resolve to the copilot without
   * preferences) and never `null` once the query has settled.
   */
  name: string | null;
  /** True only while the persona-preferences query is in flight. */
  isLoading: boolean;
}

/**
 * The resolution above, with the loading moment made visible (#1806).
 *
 * `useCompanionName()` cannot express "not known yet" — it returns a string,
 * and 20+ callers render that string into prose where a default reads fine.
 * A surface that paints IDENTITY rather than prose (the voice PiP disc paints
 * the persona's initial) must not paint the default for the second before the
 * real name arrives, so it reads this hook and shows nothing while loading.
 */
export function useCompanionNameState(): CompanionNameState {
  const { user } = useAuth();
  const { preferences, preferencesLoading } = usePersonaPreferences();

  // Staff resolve before preferences — no family context applies.
  if (user?.role === 'trfer' || user?.role === 'admin') {
    return { name: COPILOT_NAME, isLoading: false };
  }

  const personaName = preferences?.personaConfig?.personaName;
  if (personaName) {
    return { name: personaName.split(' - ')[0].trim(), isLoading: false };
  }

  if (preferencesLoading) return { name: null, isLoading: true };

  return { name: COMPANION_NAME, isLoading: false };
}

export function useCompanionName(): string {
  const { name } = useCompanionNameState();
  return name ?? COMPANION_NAME;
}

export { COMPANION_NAME as DEFAULT_COMPANION_NAME };
